import React, { useState, useEffect, useRef } from 'react';
import { ShieldAlert, ChevronDown, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useActiveInterlocks } from '../../hooks/useInterlocks';
import { useWebSocket } from '../../hooks/useWebSocket';
import { InterlockEvent } from '../../services/interlockService';
import { WebSocketMessage } from '../../types';
import { format } from 'date-fns';

interface InterlockPanelProps {
  deviceId: string | undefined;
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'text-red-400',
  high: 'text-amber-400',
  medium: 'text-teal-400',
  low: 'text-teal-300',
};

export const InterlockPanel: React.FC<InterlockPanelProps> = ({ deviceId }) => {
  const { t, i18n } = useTranslation(['components', 'pages']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: events = [] } = useActiveInterlocks(deviceId);
  const activeEvents = (events as InterlockEvent[]).filter((e) => !e.resolved_at);
  const count = activeEvents.length;

  const { on, off } = useWebSocket();

  useEffect(() => {
    const handleFired = (msg: WebSocketMessage) => {
      const key = ((msg.data as Record<string, unknown>)?.['interlock_key'] as string | undefined) ?? '';
      const label =
        i18n.exists(`pages:interlocks.keys.${key}`)
          ? t(`pages:interlocks.keys.${key}` as Parameters<typeof t>[0])
          : key;
      toast(label, {
        icon: '🛡️',
        style: { background: '#0f766e', color: '#fff' },
        duration: 5000,
      });
    };

    const handleResolved = (msg: WebSocketMessage) => {
      const key = ((msg.data as Record<string, unknown>)?.['interlock_key'] as string | undefined) ?? '';
      const label =
        i18n.exists(`pages:interlocks.keys.${key}`)
          ? t(`pages:interlocks.keys.${key}` as Parameters<typeof t>[0])
          : key;
      toast(`${t('components:interlockPanel.toastResolved', { key: label })}`, {
        icon: '✅',
        duration: 3000,
      });
    };

    on('interlock_fired', handleFired);
    on('interlock_resolved', handleResolved);

    return () => {
      off('interlock_fired', handleFired);
      off('interlock_resolved', handleResolved);
    };
  }, [on, off, t, i18n]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (count === 0) return null;

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
          'bg-teal-500/15 text-teal-400 border border-teal-500/30 hover:bg-teal-500/25',
        )}
        aria-label={t('components:interlockPanel.title')}
      >
        <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
        <span>{count}</span>
        <ChevronDown className={clsx('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className={clsx(
            'absolute right-0 top-full mt-2 w-80 z-50',
            'bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl overflow-hidden',
          )}
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-700">
            <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wide">
              {t('components:interlockPanel.title')}
            </span>
            <button
              onClick={() => {
                setOpen(false);
                navigate('/interlocks');
              }}
              className="flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300"
            >
              {t('components:interlockPanel.viewAll')}
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y divide-zinc-800">
            {activeEvents.map((event) => {
              const keyLabel = t(`pages:interlocks.keys.${event.interlock_key}` as Parameters<typeof t>[0], {
                defaultValue: event.interlock_key,
              });
              return (
                <li key={event.id} className="px-4 py-2.5">
                  <div className="flex items-start gap-2">
                    <ShieldAlert
                      className={clsx('w-3.5 h-3.5 mt-0.5 shrink-0', SEVERITY_COLOR[event.severity] ?? 'text-teal-400')}
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-zinc-100 truncate">{keyLabel}</p>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">{event.reason}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">
                        {format(new Date(event.fired_at), 'HH:mm:ss')}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
